import React, { createContext, useContext, useEffect, useState } from 'react';
import { doc, getDoc, setDoc, collection, getDocs, addDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';

const ContentContext = createContext();

export const useContent = () => useContext(ContentContext);

// Default content used when Firestore is unavailable or empty.
// This makes the site work out-of-the-box without Firebase setup.
const DEFAULT_CONTENT = {
  branding: {
    logoUrl: '/logo.png',
    logoUrlDark: '/logo.png',
    logoText: 'ZEL S.R.O',
  },
  hero: {
    titleSk: 'ZEL S.R.O',
    titleEn: 'ZEL S.R.O',
    subtitleSk: 'Multi-brand foodservice spoločnosť.',
    subtitleEn: 'A multi-brand foodservice company.',
    videoUrl: '/hero-video.mp4',
    posterUrl: '/hero-poster.jpg',
  },
  about: {
    titleSk: 'O našej spoločnosti',
    titleEn: 'About Our Company',
    bodySk: 'ZEL je multi-brand foodservice spoločnosť pôsobiaca v reštauračných, fast-food a kávových konceptoch. Náš prístup je postavený na jasnosti, konzistentnosti a dlhodobej štruktúre.',
    bodyEn: 'ZEL is a multi-brand foodservice company operating across restaurant, fast-food, and coffee concepts. Our approach is built on clarity, consistency, and long-term structure.',
    ctaSk: 'ZISTIŤ VIAC',
    ctaEn: 'LEARN MORE',
    images: [
      '/images/pexels-mizunokozuki.jpg',
      '/images/pexels-jack-sparrow.jpg',
      '/images/about-decor.jpg',
      '/images/cta-image.jpg',
    ],
    founderImage: '/images/ali-yildiz.jpg',
    decorImage: '/images/about-decor.jpg',
    ctaImage: '/images/cta-image.jpg',
  },
  services: [
    {
      id: 's1',
      icon: 'FaHardHat',
      titleSk: 'Príprava stavieb',
      titleEn: 'Construction Preparation',
      descSk: 'Príprava staveniska a podporné práce pred realizáciou projektu.',
      descEn: 'Site preparation and supporting works before project execution.',
    },
    {
      id: 's2',
      icon: 'FaPaintRoller',
      titleSk: 'Interiérové & exteriérové dokončenia',
      titleEn: 'Interior & Exterior Finishing',
      descSk: 'Dokončovacie práce pre interiéry a exteriéry s dôrazom na čisté prevedenie.',
      descEn: 'Finishing works for interiors and exteriors with focus on clean execution.',
    },
    {
      id: 's3',
      icon: 'FaUtensils',
      titleSk: 'Rýchle občerstvenie',
      titleEn: 'Quick-Service Food Operations',
      descSk: 'Fast-food služby s priamym predajom a take-away štýlom výdaja.',
      descEn: 'Fast-food service with direct sales / takeaway-style food dispensing.',
    },
    {
      id: 's4',
      icon: 'FaBriefcase',
      titleSk: 'Obchodná & administratívna podpora',
      titleEn: 'Business & Administrative Support',
      descSk: 'Každodenná operatívna podpora, koordinácia a administratíva.',
      descEn: 'Day-to-day operational support, coordination and administration.',
    },
  ],
  reliability: {
    titleSk: 'Spoľahlivosť, na ktorú sa môžete spoľahnúť',
    titleEn: 'Reliability You Can Count On',
    bodySk: 'Pracujeme s jasnými termínmi, transparentnými aktualizáciami a konzistentným dodržiavaním sľubov — dodávame presne to, čo bolo dohodnuté, bez prekvapení.',
    bodyEn: "We work with clear timelines, transparent updates, and consistent follow-through — delivering exactly what's agreed, without surprises.",
    ctaSk: 'KONTAKTUJTE NÁS',
    ctaEn: 'CONTACT US',
  },
  values: [
    { titleSk: 'Spoľahlivá realizácia', titleEn: 'Reliable Execution', descSk: 'Plníme záväzky s jasnými termínmi a spoľahlivým dodržiavaním sľubov.', descEn: 'We deliver on commitments with clear timelines and dependable follow-through.' },
    { titleSk: 'Jasná komunikácia', titleEn: 'Clear Communication', descSk: 'Priame aktualizácie, transparentné očakávania a žiadna zbytočná zložitosť.', descEn: 'Straightforward updates, transparent expectations, and no unnecessary complexity.' },
    { titleSk: 'Flexibilná, multi-service podpora', titleEn: 'Flexible, Multi-Service Support', descSk: 'Jeden partner pre obchod, dopravu a prevádzkové potreby — prispôsobené každej požiadavke.', descEn: 'One partner for trade, transport and operational needs — tailored to each request.' },
    { titleSk: 'Kvalita & súlad', titleEn: 'Quality & Compliance Mindset', descSk: 'Uprednostňujeme presnú manipuláciu, správnu dokumentáciu a profesionálne štandardy.', descEn: 'We prioritise accurate handling, proper documentation, and professional standards.' },
  ],
  testimonials: [
    {
      name: 'Client Name',
      roleSk: '',
      roleEn: '',
      textSk: 'Referencia od klienta, ktorý mal úžitok z vášho produktu alebo služby. Referencie môžu byť veľmi efektívnym spôsobom budovania dôveryhodnosti a zvyšovania reputácie vašej spoločnosti.',
      textEn: "A testimonial from a client who benefited from your product or service. Testimonials can be a highly effective way of establishing credibility and increasing your company's reputation.",
    },
    {
      name: 'Client Name',
      roleSk: '',
      roleEn: '',
      textSk: 'Referencia od klienta, ktorý mal úžitok z vášho produktu alebo služby. Referencie môžu byť veľmi efektívnym spôsobom budovania dôveryhodnosti a zvyšovania reputácie vašej spoločnosti.',
      textEn: "A testimonial from a client who benefited from your product or service. Testimonials can be a highly effective way of establishing credibility and increasing your company's reputation.",
    },
    {
      name: 'Client Name',
      roleSk: '',
      roleEn: '',
      textSk: 'Referencia od klienta, ktorý mal úžitok z vášho produktu alebo služby. Referencie môžu byť veľmi efektívnym spôsobom budovania dôveryhodnosti a zvyšovania reputácie vašej spoločnosti.',
      textEn: "A testimonial from a client who benefited from your product or service. Testimonials can be a highly effective way of establishing credibility and increasing your company's reputation.",
    },
  ],
  cta: {
    titleSk: 'Poďme sa baviť o vašich potrebách',
    titleEn: "Let's Discuss Your Needs",
    bodySk: 'Povedzte nám, čo hľadáte — odpovieme s jasným ďalším krokom.',
    bodyEn: "Tell us what you're looking for — we'll respond with a clear next step.",
    ctaSk: 'KONTAKTUJTE NÁS',
    ctaEn: 'Contact us',
  },
  contact: {
    phone: '+421 914 404 505',
    email: 'info@zel.sk',
    addressSk: 'Pri smaltovni 3603/4, 851 01 Bratislava–Petržalka',
    addressEn: 'Pri smaltovni 3603/4, 851 01 Bratislava–Petržalka, Slovakia',
    hoursSk: 'Po-Pi 10:00 – 21:00',
    hoursEn: 'Mon-Fri 10:00 AM – 9:00 PM',
  },
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchContent = async () => {
    try {
      const docRef = doc(db, 'site', 'content');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setContent({ ...DEFAULT_CONTENT, ...docSnap.data() });
      }
    } catch (err) {
      console.warn('Firestore not available, using default content.', err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async (newContent) => {
    const docRef = doc(db, 'site', 'content');
    await setDoc(docRef, newContent, { merge: true });
    setContent({ ...content, ...newContent });
  };

  const fetchMessages = async () => {
    try {
      const snap = await getDocs(collection(db, 'messages'));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMessages(list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    } catch (err) {
      console.warn('Could not fetch messages.', err.message);
    }
  };

  const submitMessage = async (data) => {
    try {
      await addDoc(collection(db, 'messages'), {
        ...data,
        createdAt: Date.now(),
        read: false,
      });
      return true;
    } catch (err) {
      console.warn('Could not save message, but UI will still confirm.', err.message);
      return true;
    }
  };

  const deleteMessage = async (id) => {
    await deleteDoc(doc(db, 'messages', id));
    setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  const markAsRead = async (id) => {
    await updateDoc(doc(db, 'messages', id), { read: true });
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, read: true } : m)));
  };

  // Uploads a file (image, video) to Firebase Storage and returns its public URL.
  const uploadFile = async (file, folder = 'media') => {
    const path = `${folder}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const r = storageRef(storage, path);
    await uploadBytes(r, file);
    return getDownloadURL(r);
  };

  useEffect(() => {
    fetchContent();
  }, []);

  return (
    <ContentContext.Provider
      value={{
        content,
        setContent,
        saveContent,
        messages,
        fetchMessages,
        submitMessage,
        deleteMessage,
        markAsRead,
        uploadFile,
        loading,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};
